import {view} from '@sail/ui/deprecated';
import * as React from 'react';
import {useAnalytics} from '../../data-layer-client/useAnalytics';
import {getOwnerWindow} from '../dom/domHelpers';

// Replicated based on https://git.corp.stripe.com/stripe-internal/pay-server/blob/HEAD/manage/frontend/src/platform/analytics/AutoAnalytics.tsx

const isInstrumentedElement = (node: HTMLElement): boolean => {
  const ownerWindow = getOwnerWindow(node);
  if (
    node instanceof ownerWindow.HTMLButtonElement ||
    node instanceof ownerWindow.HTMLAnchorElement
  ) {
    return true;
  }
  return false;
};

const getInstrumentedContainers = (element: HTMLElement) => {
  let currentElement = element.parentNode;
  const containers: Array<string> = [];

  while (
    currentElement &&
    currentElement instanceof
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      getOwnerWindow(currentElement as Element).HTMLElement
  ) {
    if (currentElement.dataset && currentElement.dataset.dbAnalyticsName) {
      containers.push(currentElement.dataset.dbAnalyticsName);
    }
    currentElement = currentElement.parentElement;
  }

  return containers;
};

const reservedDataAttributes = [
  'data-db-analytics-name',
  'data-db-analytics-key-action-name',
];

// This returns the closest parent of a node that is a button or null.
//
// In general we need to do this for the `<button><span>text</span></button>` case.
// However, we've capped the number of levels we'll traverse up the chain in order
// to avoid doing too much work, since generally there isn't a ton of nesting inside
// of a button.
export const getNearestInstrumentedElement = (
  node: HTMLElement | SVGElement,
  maxDepth = 7,
): HTMLElement | null | undefined => {
  let currentDepth = 0;
  let currentNode: HTMLElement | SVGElement | null = node;

  // `document.documentElement.parentNode.parentNode` is null, so make sure we have a node
  while (
    currentDepth < maxDepth &&
    currentNode &&
    (currentNode instanceof getOwnerWindow(currentNode).HTMLElement ||
      currentNode instanceof getOwnerWindow(currentNode).SVGElement)
  ) {
    if (
      currentNode instanceof getOwnerWindow(currentNode).HTMLElement &&
      isInstrumentedElement(currentNode)
    ) {
      return currentNode;
    }
    currentNode = currentNode.parentElement;
    currentDepth += 1;
  }

  return null;
};

// Gathers information about a button and generates a hopefully unique enough
// description of the button (together with the eventual known url it was clicked on)
// in order for a human to determine which button it is.
const generateElementData = (element: HTMLElement) => {
  const analyticsKeyActionName =
    element.getAttribute('data-db-analytics-key-action-name') || null;
  // Allow the ability to specificy the analytics name via a data attribute
  const analyticsName = element.getAttribute('data-db-analytics-name') || null;

  const shouldCaptureElementAttribute = (attr: Attr) =>
    attr &&
    attr.name &&
    typeof attr.name === 'string' &&
    attr.nodeValue &&
    attr.name.startsWith('data-db-analytics-') &&
    !reservedDataAttributes.includes(attr.name);

  // @ts-expect-error - TS2345 - Argument of type 'Location' is not assignable to parameter of type 'string | URL | undefined'.
  // eslint-disable-next-line @stripe-internal/embedded/no-restricted-globals
  const removeQueryParams = (path) => new URL(path, window.location).pathname;

  const extractAttributeNameAndValue = (attr: Attr) => [
    attr.name.replace('data-db-analytics-', ''),
    attr.name === 'href' ? removeQueryParams(attr.nodeValue) : attr.nodeValue,
  ];

  const additionalAnalytics = Object.fromEntries(
    Array.from(element.attributes)
      .filter(shouldCaptureElementAttribute)
      .map(extractAttributeNameAndValue),
  );

  const instrumentedContainers = getInstrumentedContainers(element);
  const domId = element.id || null;
  const rect = element.getBoundingClientRect();
  const pos = `${Math.round(rect.left)}x${Math.round(rect.top)}`;

  return {
    analytics_name: analyticsName,
    analytics_key_action_name: analyticsKeyActionName,
    additional_analytics: additionalAnalytics,
    auto_track: true,
    dom_id: domId,
    pos,
    containers: instrumentedContainers,
  };
};

export const AutoAnalyticsProvider: React.FC = ({children}) => {
  const {action} = useAnalytics();

  const handleClick = (event: Event) => {
    // Check if we're part of a button
    if (
      !(
        event.target instanceof
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          getOwnerWindow(event.target as Element | null).HTMLElement ||
        event.target instanceof
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          getOwnerWindow(event.target as Element | null).SVGElement
      )
    ) {
      return;
    }

    const element = getNearestInstrumentedElement(event.target);
    const ownerWindow = getOwnerWindow(element);

    // Be protective of elements that block analytics
    if (element && element instanceof ownerWindow.HTMLElement) {
      if (
        element instanceof ownerWindow.HTMLButtonElement &&
        (element.disabled ||
          element.attributes.getNamedItem('aria-disabled')?.value === 'true')
      ) {
        return;
      }

      if (
        element instanceof ownerWindow.HTMLAnchorElement &&
        element.attributes.getNamedItem('aria-disabled')?.value === 'true'
      ) {
        return;
      }

      const elementData = generateElementData(element);
      if (elementData.analytics_name || elementData.analytics_key_action_name) {
        if (elementData.analytics_key_action_name) {
          action(
            `key_action.${elementData.analytics_key_action_name}.click`,
            elementData,
          );
        }
        if (elementData.analytics_name) {
          action(elementData.analytics_name, elementData);
        }
      }
    }
  };

  return (
    <view.div
      // We use onPointerDownCapture to intercept click events before they are processed
      // Note this will catch events under the react tree, which includes portaled elements
      // like dialog, popover, drawer and tooltip
      // We use onPointerDownCapture here instead of onClickCapture to also capture events from onPress
      onPointerDownCapture={(ev) => {
        // onPointerDownCapture will capture left, middle, and right clicks mapping to a `button`
        // attribute of 0, 1, 2 respectively. We need to filter only for left clicks (ie button = 0)
        if (ev.button === 0) {
          handleClick(ev.nativeEvent);
        }
      }}
    >
      {children}
    </view.div>
  );
};
